import React from 'react';
import { Navigate, createBrowserRouter } from 'react-router-dom';

import { PERMISSION_ACTIONS } from 'data/permissionActions';
import { PERMISSION_SUBJECTS } from 'data/permissionSubjects';
import AssetSidebar from 'components/AssetSidebar';
import ASLToolPage from 'pages/ASLToolPage';
import ASLVideoUploadPage from 'pages/ASLVideoUploadPage';
import ASLVideoHistoryPage from 'pages/ASLVideoHistoryPage';
import BatchAllocationPage from 'pages/BatchAllocationPage';
import ClassesSidebar from 'components/ClassesSidebar';
import CommunicationSidebar from 'components/CommunicationSidebar';
import CourseManagementPage from 'pages/CourseManagementPage';
import CoursePauseApprovedPage from 'pages/CoursePauseApprovedPage';
import CoursePausePage from 'pages/CoursePausePage';
import CoursePausePendingPage from 'pages/CoursePausePendingPage';
import CoursePauseRequestedPage from 'pages/CoursePauseRequestedPage';
import CoursePauseRequestStatusPage from 'pages/CoursePauseRequestStatusPage';
import CoursePauseScheduledPage from 'pages/CoursePauseScheduledPage';
import CoursePauseToBeResumedPage from 'pages/CoursePauseToBeResumedPage';
import CreateClassPage from 'pages/CreateClassPage';
import CreateLibraryPage from 'pages/CreateLibraryPage';
import CreditManagementPage from 'pages/CreditManagementPage';
import CreditManagementSidebar from 'components/CreditManagementSidebar';
import EditClassPage from 'pages/EditClassPage';
import EditClassPlaylistPage from 'pages/EditClassPlaylistPage';
import EditCrashCourseBatchPage from 'pages/EditCrashCourseBatchPage';
import EditLibraryPage from 'pages/EditLibraryPage';
import EditSbatGroupPage from 'pages/EditSbatGroupPage';
import EditSbatGroupPlaylistPage from 'pages/EditSbatGroupPlaylistPage';
import HistoryListener from 'components/HistoryListener';
import ManageBatchModulePage from 'pages/ManageBatchModulePage';
import ManageMenteeModulesPage from 'pages/ManageMenteeModulesPage';
import ModuleManagementSidebar from 'components/ModuleManagementSidebar';
import NeoVarsityManagementSidebar from 'components/NeovarsityManagementSidebar';
import NeovarsitySyncUserActivitiesPage from 'pages/NeovarsitySyncUserActivitiesPage';
import NeovarsityManagementPage from 'pages/NeovarsityManagementPage';
import NeovarsityManagementPendingPage from './pages/NeovarsityManagementPendingPage';
import NeovarsityManagementSubmittedPage from 'pages/NeovarsityManagementSubmittedPage';
import PersonalisedMenteeEmailersPage from 'pages/PersonalisedMenteeEmailersPage';
import PlaylistPreviewPage from 'pages/PlaylistPreviewPage';
import ProtectedRoute from 'components/ProtectedRoute';
import RecordingsSidebar from 'components/RecordingsSidebar';
import RescheduleClassesPage from 'pages/ResheduleClassesPage';
import ResetSatPage from 'pages/ResetSatPage';
import SatSidebar from 'components/SatSidebar';
import ScheduleClassesPage from 'pages/ScheduleClassesPage';
import SelectBatchModulePage from 'pages/SelectBatchModulePage';
import SelectBatchPage from 'pages/SelectBatchPage';
import SelectClassPage from 'pages/SelectClassPage';
import SelectLibraryPage from 'pages/SelectLibraryPage';
import SelectMenteePage from 'pages/SelectMenteePage';
import SentryErrorBoundaryWrapper from 'components/SentryErrorBoundaryWrapper';
import SuperBatchManagementSidebar from 'components/SuperBatchManagementSidebar';
import ViewSchedulePage from 'pages/ViewSchedulePage';
import ViewPendingRecordingRequestsPage from 'pages/ViewPendingRecordingRequestsPage';
import UploadVideoPage from 'pages/UploadVideoPage';
import SuperBatchUpdateMetaPage from 'pages/SuperBatchUpdateMetaPage';

export const AppRoutes = createBrowserRouter(
  [
    {
      element: <HistoryListener />,
      errorElement: <SentryErrorBoundaryWrapper />,
      children: [
        {
          element: <CourseManagementPage />,
          path: '/',
        },
        // Classroom Creation Tool Routes
        {
          path: '/classes',
          element: <ClassesSidebar />,
          children: [
            {
              path: '/classes',
              element: (
                <Navigate
                  to="view-schedule"
                  replace
                />
              ),
            },
            {
              path: 'view-schedule',
              element: (
                <ProtectedRoute
                  permissions={[
                    PERMISSION_ACTIONS.manage,
                    PERMISSION_ACTIONS.read,
                  ]}
                  subject={PERMISSION_SUBJECTS.sbatGroup}
                >
                  <SelectBatchPage />
                </ProtectedRoute>
              ),
              children: [
                {
                  path: ':superBatchId',
                  element: (
                    <ProtectedRoute
                      permissions={[
                        PERMISSION_ACTIONS.manage,
                        PERMISSION_ACTIONS.read,
                      ]}
                      subject={PERMISSION_SUBJECTS.sbatGroup}
                    >
                      <ViewSchedulePage />
                    </ProtectedRoute>
                  ),
                },
              ],
            },
            {
              path: 'edit-sbat-group/:sbatGroupId',
              element: (
                <ProtectedRoute
                  permissions={[
                    PERMISSION_ACTIONS.read,
                    PERMISSION_ACTIONS.manage,
                  ]}
                  subject={PERMISSION_SUBJECTS.sbatGroup}
                >
                  <EditSbatGroupPage />
                </ProtectedRoute>
              ),
            },
            {
              path: 'reschedule/:superBatchId',
              element: (
                <ProtectedRoute
                  permissions={[
                    PERMISSION_ACTIONS.read,
                    PERMISSION_ACTIONS.manage,
                  ]}
                  subject={PERMISSION_SUBJECTS.sbatGroup}
                >
                  <RescheduleClassesPage />
                </ProtectedRoute>
              ),
            },
            {
              path: 'schedule-classes',
              element: (
                <ProtectedRoute
                  permissions={[
                    PERMISSION_ACTIONS.create,
                    PERMISSION_ACTIONS.manage,
                  ]}
                  subject={PERMISSION_SUBJECTS.sbatGroup}
                >
                  <ScheduleClassesPage />
                </ProtectedRoute>
              ),
            },
            {
              path: 'create-library',
              element: (
                <ProtectedRoute
                  permissions={[
                    PERMISSION_ACTIONS.create,
                    PERMISSION_ACTIONS.manage,
                  ]}
                  subject={PERMISSION_SUBJECTS.library}
                >
                  <CreateLibraryPage />
                </ProtectedRoute>
              ),
            },
            {
              path: 'edit-library',
              element: (
                <ProtectedRoute
                  permissions={[
                    PERMISSION_ACTIONS.manage,
                    PERMISSION_ACTIONS.read,
                  ]}
                  subject={PERMISSION_SUBJECTS.library}
                >
                  <SelectLibraryPage />
                </ProtectedRoute>
              ),
              children: [
                {
                  path: ':libraryId',
                  element: (
                    <ProtectedRoute
                      permissions={[
                        PERMISSION_ACTIONS.manage,
                        PERMISSION_ACTIONS.read,
                      ]}
                      subject={PERMISSION_SUBJECTS.library}
                    >
                      <EditLibraryPage />
                    </ProtectedRoute>
                  ),
                },
              ],
            },
            {
              path: 'edit-super-batch',
              element: (
                <ProtectedRoute
                  permissions={[
                    PERMISSION_ACTIONS.manage,
                    PERMISSION_ACTIONS.read,
                  ]}
                  subject={PERMISSION_SUBJECTS.superBatch}
                >
                  <EditCrashCourseBatchPage />
                </ProtectedRoute>
              ),
            },
            {
              path: 'create-class',
              element: (
                <ProtectedRoute
                  permissions={[
                    PERMISSION_ACTIONS.manage,
                    PERMISSION_ACTIONS.create,
                  ]}
                  subject={PERMISSION_SUBJECTS.academyTopic}
                >
                  <CreateClassPage />
                </ProtectedRoute>
              ),
            },
            {
              path: 'edit-classes',
              element: (
                <ProtectedRoute
                  permissions={[
                    PERMISSION_ACTIONS.manage,
                    PERMISSION_ACTIONS.read,
                  ]}
                  subject={PERMISSION_SUBJECTS.academyTopic}
                >
                  <SelectClassPage />
                </ProtectedRoute>
              ),
              children: [
                {
                  path: ':academyTopicId',
                  element: (
                    <ProtectedRoute
                      permissions={[
                        PERMISSION_ACTIONS.manage,
                        PERMISSION_ACTIONS.read,
                      ]}
                      subject={PERMISSION_SUBJECTS.academyTopic}
                    >
                      <EditClassPage />
                    </ProtectedRoute>
                  ),
                },
              ],
            },
            {
              path: 'view-pending-requests',
              element: (
                <ProtectedRoute
                  permissions={[
                    PERMISSION_ACTIONS.manage,
                    PERMISSION_ACTIONS.read,
                  ]}
                  subject={PERMISSION_SUBJECTS.recordingRequest}
                >
                  <ViewPendingRecordingRequestsPage />
                </ProtectedRoute>
              ),
            },
          ],
        },
        {
          path: '/classes/edit-sbat-group/:sbatGroupId/playlist',
          element: (
            <ProtectedRoute
              permissions={[PERMISSION_ACTIONS.read, PERMISSION_ACTIONS.manage]}
              subject={PERMISSION_SUBJECTS.playlist}
            >
              <EditSbatGroupPlaylistPage />
            </ProtectedRoute>
          ),
        },
        {
          path: '/classes/edit-classes/:academyTopicId/playlist',
          element: (
            <ProtectedRoute
              permissions={[PERMISSION_ACTIONS.manage, PERMISSION_ACTIONS.read]}
              subject={PERMISSION_SUBJECTS.playlist}
            >
              <EditClassPlaylistPage />
            </ProtectedRoute>
          ),
        },
        {
          path: '/playlist/:playlistId',
          element: (
            <ProtectedRoute
              permissions={[PERMISSION_ACTIONS.manage, PERMISSION_ACTIONS.read]}
              subject={PERMISSION_SUBJECTS.playlist}
            >
              <PlaylistPreviewPage />
            </ProtectedRoute>
          ),
        },
        // Course Resume Tool Routes
        {
          path: '/course-pause',
          element: (
            <ProtectedRoute
              permissions={[PERMISSION_ACTIONS.manage, PERMISSION_ACTIONS.read]}
              subject={PERMISSION_SUBJECTS.coursePauseRequest}
            >
              <CoursePausePage />
            </ProtectedRoute>
          ),
          children: [
            {
              path: '/course-pause',
              element: (
                <Navigate
                  to="/course-pause/future-dated/to-be-resumed"
                  replace
                />
              ),
            },
            {
              path: ':requestType',
              element: (
                <ProtectedRoute
                  permissions={[
                    PERMISSION_ACTIONS.manage,
                    PERMISSION_ACTIONS.read,
                  ]}
                  subject={PERMISSION_SUBJECTS.coursePauseRequest}
                >
                  <CoursePauseRequestStatusPage />
                </ProtectedRoute>
              ),
              children: [
                {
                  path: 'approved',
                  element: (
                    <ProtectedRoute
                      permissions={[
                        PERMISSION_ACTIONS.manage,
                        PERMISSION_ACTIONS.read,
                      ]}
                      subject={PERMISSION_SUBJECTS.coursePauseRequest}
                    >
                      <CoursePauseApprovedPage />
                    </ProtectedRoute>
                  ),
                },
                {
                  path: 'requested',
                  element: (
                    <ProtectedRoute
                      permissions={[
                        PERMISSION_ACTIONS.manage,
                        PERMISSION_ACTIONS.read,
                      ]}
                      subject={PERMISSION_SUBJECTS.coursePauseRequest}
                    >
                      <CoursePauseRequestedPage />,
                    </ProtectedRoute>
                  ),
                },
                {
                  path: 'pending',
                  element: (
                    <ProtectedRoute
                      permissions={[
                        PERMISSION_ACTIONS.manage,
                        PERMISSION_ACTIONS.read,
                      ]}
                      subject={PERMISSION_SUBJECTS.coursePauseRequest}
                    >
                      <CoursePausePendingPage />,
                    </ProtectedRoute>
                  ),
                },
                {
                  path: 'to-be-resumed',
                  element: (
                    <ProtectedRoute
                      permissions={[
                        PERMISSION_ACTIONS.manage,
                        PERMISSION_ACTIONS.read,
                      ]}
                      subject={PERMISSION_SUBJECTS.coursePauseRequest}
                    >
                      <CoursePauseToBeResumedPage />
                    </ProtectedRoute>
                  ),
                },
                {
                  path: 'scheduled',
                  element: (
                    <ProtectedRoute
                      permissions={[
                        PERMISSION_ACTIONS.manage,
                        PERMISSION_ACTIONS.read,
                      ]}
                      subject={PERMISSION_SUBJECTS.coursePauseRequest}
                    >
                      <CoursePauseScheduledPage />
                    </ProtectedRoute>
                  ),
                },
              ],
            },
          ],
        },
        //SAT Management Tool Routes
        {
          path: '/sat-management',
          element: <SatSidebar />,
          children: [
            {
              path: 'reset-sat',
              element: (
                <ProtectedRoute
                  permissions={[
                    PERMISSION_ACTIONS.manage,
                    PERMISSION_ACTIONS.read,
                  ]}
                  subject={PERMISSION_SUBJECTS.courseAssessmentMentee}
                >
                  <ResetSatPage />
                </ProtectedRoute>
              ),
            },
            {
              path: 'batch-allocation',
              element: (
                <ProtectedRoute
                  permissions={[
                    PERMISSION_ACTIONS.manage,
                    PERMISSION_ACTIONS.read,
                  ]}
                  subject={PERMISSION_SUBJECTS.courseAssessmentMentee}
                >
                  <BatchAllocationPage />
                </ProtectedRoute>
              ),
            },
          ],
        },
        // Module Recording Tool Routes
        {
          element: <RecordingsSidebar />,
          children: [
            {
              path: '/recordings/view-pending-requests',
              element: (
                <ProtectedRoute
                  permissions={[
                    PERMISSION_ACTIONS.manage,
                    PERMISSION_ACTIONS.read,
                  ]}
                  subject={PERMISSION_SUBJECTS.recordingRequest}
                >
                  <ViewPendingRecordingRequestsPage />
                </ProtectedRoute>
              ),
            },
          ],
        },
        // Asset Management Tool Routes
        {
          path: '/asset-management',
          element: <AssetSidebar />,
          children: [
            {
              path: '/asset-management',
              element: (
                <Navigate
                  to="upload-video"
                  replace
                />
              ),
            },
            {
              path: 'upload-video',
              element: (
                <ProtectedRoute
                  permissions={[
                    PERMISSION_ACTIONS.manage,
                    PERMISSION_ACTIONS.read,
                  ]}
                  subject={PERMISSION_SUBJECTS.rawAsset}
                >
                  <UploadVideoPage />
                </ProtectedRoute>
              ),
            },
            {
              path: 'bulk-upload-video',
              element: (
                <ProtectedRoute
                  permissions={[
                    PERMISSION_ACTIONS.manage,
                    PERMISSION_ACTIONS.read,
                  ]}
                  subject={PERMISSION_SUBJECTS.rawAsset}
                >
                  <ASLToolPage />
                </ProtectedRoute>
              ),
              children: [
                {
                  path: '/asset-management/bulk-upload-video',
                  element: (
                    <Navigate
                      to="/asset-management/bulk-upload-video/video-upload-history"
                      replace
                    />
                  ),
                },
                {
                  path: 'video-upload-history',
                  element: (
                    <ProtectedRoute
                      permissions={[
                        PERMISSION_ACTIONS.manage,
                        PERMISSION_ACTIONS.read,
                      ]}
                      subject={PERMISSION_SUBJECTS.rawAsset}
                    >
                      <ASLVideoHistoryPage />
                    </ProtectedRoute>
                  ),
                },
                {
                  path: 'upload-video',
                  element: (
                    <ProtectedRoute
                      permissions={[
                        PERMISSION_ACTIONS.manage,
                        PERMISSION_ACTIONS.read,
                      ]}
                      subject={PERMISSION_SUBJECTS.rawAsset}
                    >
                      <ASLVideoUploadPage />
                    </ProtectedRoute>
                  ),
                },
              ],
            },
          ],
        },
        // Communication Management Tool Routes
        {
          path: '/communication-management',
          element: <CommunicationSidebar />,
          children: [
            {
              path: '/communication-management',
              element: (
                <Navigate
                  to="personalised-mentee-mailer/"
                  replace
                />
              ),
            },
            {
              path: 'personalised-mentee-mailer/',
              element: (
                <ProtectedRoute
                  permissions={[
                    PERMISSION_ACTIONS.manage,
                    PERMISSION_ACTIONS.read,
                  ]}
                  subject={PERMISSION_SUBJECTS.blacklist}
                >
                  <PersonalisedMenteeEmailersPage />
                </ProtectedRoute>
              ),
            },
          ],
        },
        // Module Management Tool Routes
        {
          path: '/module-management',
          element: <ModuleManagementSidebar />,
          children: [
            {
              path: '/module-management',
              element: (
                <Navigate
                  to="mentee"
                  replace
                />
              ),
            },
            {
              path: 'mentee',
              element: (
                <ProtectedRoute
                  permissions={[
                    PERMISSION_ACTIONS.manage,
                    PERMISSION_ACTIONS.read,
                  ]}
                  subject={PERMISSION_SUBJECTS.menteeModule}
                >
                  <SelectMenteePage />
                </ProtectedRoute>
              ),
              children: [
                {
                  path: ':email',
                  element: (
                    <ProtectedRoute
                      permissions={[
                        PERMISSION_ACTIONS.manage,
                        PERMISSION_ACTIONS.read,
                      ]}
                      subject={PERMISSION_SUBJECTS.menteeModule}
                    >
                      <ManageMenteeModulesPage />
                    </ProtectedRoute>
                  ),
                },
              ],
            },
            {
              path: 'batch',
              element: (
                <ProtectedRoute
                  permissions={[
                    PERMISSION_ACTIONS.manage,
                    PERMISSION_ACTIONS.read,
                  ]}
                  subject={PERMISSION_SUBJECTS.superBatchModule}
                >
                  <SelectBatchModulePage />
                </ProtectedRoute>
              ),
              children: [
                {
                  path: ':batch',
                  element: (
                    <ProtectedRoute
                      permissions={[
                        PERMISSION_ACTIONS.manage,
                        PERMISSION_ACTIONS.read,
                      ]}
                      subject={PERMISSION_SUBJECTS.superBatchModule}
                    >
                      <ManageBatchModulePage />
                    </ProtectedRoute>
                  ),
                },
              ],
            },
          ],
        },
        // Neovarsity Management Tool Routes
        {
          path: '/neovarsity',
          element: <NeoVarsityManagementSidebar />,
          children: [
            {
              path: '/neovarsity',
              element: (
                <Navigate
                  to="/neovarsity/projects/submitted"
                  replace
                />
              ),
            },
            {
              path: 'projects',
              element: (
                <ProtectedRoute
                  permissions={[
                    PERMISSION_ACTIONS.manage,
                    PERMISSION_ACTIONS.read,
                  ]}
                  subject={PERMISSION_SUBJECTS.capstoneSubmission}
                >
                  <NeovarsityManagementPage />
                </ProtectedRoute>
              ),
              children: [
                {
                  path: 'pending',
                  element: (
                    <ProtectedRoute
                      permissions={[
                        PERMISSION_ACTIONS.manage,
                        PERMISSION_ACTIONS.read,
                      ]}
                      subject={PERMISSION_SUBJECTS.capstoneSubmission}
                    >
                      <NeovarsityManagementPendingPage />
                    </ProtectedRoute>
                  ),
                },
                {
                  path: 'submitted',
                  element: (
                    <ProtectedRoute
                      permissions={[
                        PERMISSION_ACTIONS.manage,
                        PERMISSION_ACTIONS.read,
                      ]}
                      subject={PERMISSION_SUBJECTS.capstoneSubmission}
                    >
                      <NeovarsityManagementSubmittedPage />
                    </ProtectedRoute>
                  ),
                },
              ],
            },
            {
              path: 'sync-user-activities',
              element: (
                <ProtectedRoute
                  permissions={[
                    PERMISSION_ACTIONS.manage,
                    PERMISSION_ACTIONS.read,
                  ]}
                  subject={PERMISSION_SUBJECTS.capstoneSubmission}
                >
                  <NeovarsitySyncUserActivitiesPage />
                </ProtectedRoute>
              ),
            },
          ],
        },
        // Credit Management Tool Routes
        {
          path: '/credit-management',
          element: <CreditManagementSidebar />,
          children: [
            {
              path: '/credit-management',
              element: (
                <Navigate
                  to="manage-credit/"
                  replace
                />
              ),
            },
            {
              path: 'manage-credit/',
              element: (
                <ProtectedRoute
                  permissions={[
                    PERMISSION_ACTIONS.manage,
                    PERMISSION_ACTIONS.read,
                  ]}
                  subject={PERMISSION_SUBJECTS.userCredit}
                >
                  <CreditManagementPage />
                </ProtectedRoute>
              ),
            },
          ],
        },
        // Super Batch Management Tool Routes
        {
          path: '/super-batch-management',
          element: <SuperBatchManagementSidebar />,
          children: [
            {
              path: '/super-batch-management',
              element: (
                <Navigate
                  to="update-meta/"
                  replace
                />
              ),
            },
            {
              path: 'update-meta/',
              element: (
                <ProtectedRoute
                  permissions={[
                    PERMISSION_ACTIONS.manage,
                    PERMISSION_ACTIONS.read,
                    PERMISSION_ACTIONS.update,
                  ]}
                  subject={PERMISSION_SUBJECTS.superBatch}
                >
                  <SuperBatchUpdateMetaPage />
                </ProtectedRoute>
              ),
            },
          ],
        },
      ],
    },
  ],
  {
    basename: process.env.REACT_APP_FRONTEND_BASE_PATH,
  }
);
